import {
    createAnalytics, createIdentityJwtTokenGetter
} from '@3plearning/mx-teacher-analytics';
import type { AnalyticsClient } from '@3plearning/mx-teacher-analytics';
import { AnalyticsProvider } from '@3plearning/mx-teacher-analytics/react';
import React, {
    PropsWithChildren, useRef, useState
} from 'react';

import { resolveAnalyticsConfig } from './config';
import { appEvents } from './events';
import type { AppEvents } from './events';
import { resolveEnabledGroups } from './feature-groups';
import { selectAuthStateToken } from '@config/auth/auth-slice';
import { useEnvConfig } from '@config/env/env-config-provider';
import { useAppSelector } from '@config/react-redux/hooks';
import { QueryParams } from '@config/react-router-dom/hooks/query-params.types';
import { useQueryParams } from '@config/react-router-dom/hooks/use-query-params';

const APP_NAME = 'combined-assign-review';
const TEACHER_TOTTER_PRODUCT = 'mathletics';

type BuildParams = {
    environment: string
    posthogEnabled: boolean
    trackingGroups: string | undefined
    teacherTotterEnabled: boolean
    teacherTotterSyncUrl: string
    identityGatewayUrl: string
    getAppAuthToken: () => string | null
};

// TeacherTotter is only permitted for OneRoster-activated schools, and only
// when a sync URL is configured (the package throws when enabled without one).
// Analytics must never break the app, so a failed build falls back to
// teacher-totter off.
const buildAnalyticsClient = (params: BuildParams): AnalyticsClient<AppEvents> => {
    const {
        environment,
        posthogEnabled,
        trackingGroups,
        teacherTotterEnabled,
        teacherTotterSyncUrl,
        identityGatewayUrl,
        getAppAuthToken
    } = params;
    const analyticsConfig = {
        app: APP_NAME,
        environment,
        providers: {
            posthog: { enabled: posthogEnabled },
            teacherTotter: {
                enabled: teacherTotterEnabled && Boolean(teacherTotterSyncUrl),
                syncUrl: teacherTotterSyncUrl,
                product: TEACHER_TOTTER_PRODUCT,
                getAuthToken: createIdentityJwtTokenGetter({
                    identityGatewayUrl,
                    getAppAuthToken
                })
            }
        },
        enabledFeatureGroups: resolveEnabledGroups(trackingGroups),
        events: appEvents
    };

    try {
        return createAnalytics(analyticsConfig);
    } catch (error) {
        console.error('[analytics] Initialisation failed; continuing with teacher-totter disabled.', error);

        return createAnalytics({
            ...analyticsConfig,
            providers: {
                ...analyticsConfig.providers,
                teacherTotter: {
                    ...analyticsConfig.providers.teacherTotter,
                    enabled: false
                }
            }
        });
    }
};

export const AnalyticsAppProvider = ({ children }: PropsWithChildren<unknown>) => {
    const envConfig = useEnvConfig();
    const { ENVIRONMENT } = envConfig;
    const queryParams = useQueryParams();
    const authToken = useAppSelector(selectAuthStateToken);
    const authTokenRef = useRef<string | null>(null);

    authTokenRef.current = authToken ?? null;

    const isOneRosterActivatedSchool = queryParams.get(QueryParams.isOneRosterActivatedSchool) === 'true';
    const analyticsConfig = resolveAnalyticsConfig(envConfig);
    // Build the client exactly once. The provider only mounts once the app has
    // rendered, so the values above are already resolved; the live auth token
    // is read through authTokenRef so it stays current.
    const [client] = useState(() => buildAnalyticsClient({
        environment: ENVIRONMENT,
        posthogEnabled: analyticsConfig.posthogEnabled,
        trackingGroups: analyticsConfig.posthogTrackingGroups,
        teacherTotterEnabled: analyticsConfig.teacherTotterEnabled && isOneRosterActivatedSchool,
        teacherTotterSyncUrl: analyticsConfig.teacherTotterSyncUrl,
        identityGatewayUrl: analyticsConfig.identityGatewayUrl,
        getAppAuthToken: () => authTokenRef.current
    }));

    return (
        <AnalyticsProvider client={client}>
            {children}
        </AnalyticsProvider>
    );
};
